import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { ButtonColorScheme, ButtonSize, ButtonVariant } from '../types';

interface UseVariantStyleProps {
	colorScheme: ButtonColorScheme;
	size?: ButtonSize;
	variant?: ButtonVariant;
}

const switchColor = (props: { colorScheme: ButtonColorScheme }) => {
	const color = (primary: string, secondary: string, danger: string) => {
		switch (props.colorScheme) {
			case 'primary':
				return primary;
			case 'danger':
				return danger;
			default:
				return secondary;
		}
	};

	color.toString = () => props.colorScheme;

	return color;
};

export const useVariantStyle = createUseVariantStyle<UseVariantStyleProps>({
	size: {
		xs: {
			borderRadius: 'sm',
			fontSize: 'sm',
			h: rem(24),
			lineHeight: 1.43,
			px: 2,
		},
		sm: {
			borderRadius: 'sm',
			fontSize: 'md',
			h: rem(32),
			px: 3,
		},
		md: {
			borderRadius: 'md',
			fontSize: 'md',
			h: rem(40),
			px: 4,
		},
		lg: {
			borderRadius: 'md',
			fontSize: 'lg',
			h: rem(44),
			lineHeight: 1.56,
			px: 4,
		},
		xl: {
			borderRadius: 'md',
			fontSize: 'lg',
			h: rem(48),
			px: 5,
		},
	},
	variant: {
		solid: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: color('primary.bg', 'text.default', 'danger.bg'),
				color: 'text.onColor',
				_hover: {
					bg: color('primary.bgHover', `${color}.bgHover`, 'danger.bgHover'),
					_disabled: {
						bg: 'secondary.bgAlpha',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: color('primary.bgActive', `${color}.bgActive`, 'danger.bgActive'),
				},
				_disabled: {
					bg: 'secondary.bgAlpha',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		alpha: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: color('primary.bgAlpha', `${color}.bgAlpha`, 'danger.bgAlpha'),
				color: color('primary.fgStrong', 'secondary.fg', 'danger.fgStrong'),
				_hover: {
					bg: color('primary.bgAlphaHover', `${color}.bgAlphaHover`, 'danger.bgAlphaHover'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
					_disabled: {
						bg: 'secondary.bgAlpha',
						color: 'text.disabled',
						cursor: 'not-allowed',
					},
				},
				_active: {
					bg: color('primary.bgAlphaActive', `${color}.bgAlphaActive`, 'danger.bgAlphaActive'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
				},
				_disabled: {
					bg: 'secondary.bgAlpha',
					color: 'text.disabled',
				},
			};
		},
		ghost: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fgStrong', 'secondary.fg', 'danger.fgStrong'),
				_hover: {
					bg: color('primary.bgAlpha', `${color}.bgAlpha`, 'danger.bgAlpha'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
					_disabled: {
						bg: 'transparent',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: color('primary.bgAlphaHover', `${color}.bgAlphaHover`, 'danger.bgAlphaHover'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
				},
				_disabled: {
					bg: 'transparent',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		link: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fg', 'text.softest', 'danger.fg'),
				h: 'auto',
				p: 0,
				verticalAlign: 'baseline',
				_hover: {
					color: color('primary.fgStrong', 'text.default', 'danger.fgStrong'),
					textDecoration: 'underline',
					_disabled: {
						color: 'text.disabled',
					},
				},
				_active: {
					color: color('primary.fgStronger', 'text.default', 'danger.fgStronger'),
				},
				_disabled: {
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		outline: ({ props }) => {
			const color = switchColor(props);

			return {
				bg: 'transparent',
				color: color('primary.fgStrong', 'secondary.fg', 'danger.fgStrong'),
				borderColor: color('primary.fgStrong', 'secondary.fg', 'danger.fgStrong'),
				borderWidth: 'thin',
				_hover: {
					bg: color('primary.bgAlpha', `${color}.bgAlpha`, 'danger.bgAlpha'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
					_disabled: {
						bg: 'transparent',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: color('primary.bgAlphaHover', `${color}.bgAlphaHover`, 'danger.bgAlphaHover'),
					color: color('primary.fgStronger', 'secondary.fgActive', 'danger.fgStronger'),
				},
				_disabled: {
					bg: 'transparent',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
		subtle: () => {
			return {
				bg: 'bg.subtle',
				color: 'text.default',
				_hover: {
					bg: 'gray.100',
					color: 'text.default',
					_disabled: {
						bg: 'bg.subtle',
						color: 'text.disabled',
					},
				},
				_active: {
					bg: 'gray.200',
					color: 'text.default',
				},
				_disabled: {
					bg: 'bg.subtle',
					color: 'text.disabled',
					cursor: 'not-allowed',
				},
			};
		},
	},
});
